// MA-892 — badge persistence (KID-JOURNEY-2026-10-08 D-KJ-9, D-KJ-11).
// Spec: docs/build/specs/MA-892-badge-persistence.md.
//
// A badge is a record that is never taken away. Each newly earned badge record
// (an MA-891 `earnedIds` entry: `skill:<id>:silver|gold`, `chapter:<trophy>`,
// `days:<n>`, `days:<n>#<k>`) writes ONE `badge_earned` milestone, local first
// and then cloud, through the existing milestone helpers (buildBadgeEarnedEvent
// + logMilestoneEvent, appendLocalMilestone, the outbox drain). The book is
// derived-now ∪ latched, built by MA-891's pure buildBadgeBook (never
// re-implemented here).
//
// The practice-day count reads session_completed from a COMPLETE source: the
// cloud rows paged until exhausted (fetchAllMilestoneEventsOfTypesOrThrow),
// merged with the local mirror by mergeMilestones, which keeps the CLOUD copy
// of a marker (server event_at) over its local mirror (client clock).

import type { ChildAttempt } from "@/adaptation/types";
import {
  buildBadgeEarnedEvent,
  fetchAllMilestoneEventsOfTypesOrThrow,
  logMilestoneEvent,
  type MilestoneEvent,
} from "../milestoneLogger";
import {
  appendLocalMilestone,
  loadLocalMilestones,
  markMilestonesConfirmed,
  mergeMilestones,
} from "../localMilestoneStore";
import { fetchTierHistoryForChildOrThrow, type TierHistoryRead } from "../attemptLogger";
import { loadLocalAttempts, mergeAttempts } from "../localAttemptStore";
import { buildBadgeBook, type BadgeBook, type BadgeBookInput } from "./index";

/** The milestone types the badge book reads. */
export const BADGE_SOURCE_EVENT_TYPES = ["session_completed", "badge_earned"] as const;

/** Badge record ids latched by badge_earned rows. */
export function latchedBadgeIdsFromEvents(events: readonly MilestoneEvent[]): Set<string> {
  const out = new Set<string>();
  for (const e of events) {
    if (e.eventType !== "badge_earned") continue;
    const id = (e.contextData ?? {}).badgeId;
    if (typeof id === "string" && id !== "") out.add(id);
  }
  return out;
}

/** event_at of every session_completed marker in an already-merged list. */
export function sessionCompletedTimes(events: readonly MilestoneEvent[]): string[] {
  return events
    .filter((e) => e.eventType === "session_completed" && typeof e.eventAt === "string" && e.eventAt !== "")
    .map((e) => e.eventAt as string);
}

export interface BadgeSources {
  /** Cloud ∪ local attempts (mergeAttempts), as the tree reads them. */
  attempts: ChildAttempt[];
  /** Cloud session_completed + badge_earned rows (complete, paged). */
  cloudEvents: MilestoneEvent[];
  /** MA-895 (RED round 1 MUST-2, operator decision D-1020-7): true when the
   *  cloud milestone read was attempted for a linked child and FAILED, so
   *  `cloudEvents` is empty for a reason other than "no rows". The book still
   *  degrades to the local mirror; a consumer that must not mistake a missing
   *  latch for a new badge (the recap reveal) reads this and reveals nothing. */
  cloudEventsFailed: boolean;
}

export interface LoadBadgeSourcesDeps {
  fetchAttempts?: (kidId: string) => Promise<ChildAttempt[]>;
  /** MA-1202 RED r1 S-1: the shared tier-history read (useBadges passes
   *  tierHistoryReader(queryClient, ...)); a failure still swallows to no
   *  cloud rows. Omitted, a direct read. Ignored when `fetchAttempts` is set. */
  readTierHistory?: TierHistoryRead;
  /** Test seam: the data client the paged milestone reader queries. */
  eventsClient?: Parameters<typeof fetchAllMilestoneEventsOfTypesOrThrow>[3];
}

/** The async reads: attempts as useSkillGraph reads them (MA-1202: the full
 *  cloud history ∪ the whole local mirror), and the COMPLETE cloud session_completed/badge_earned history. A
 *  failed cloud event read degrades to the local mirror (it can only
 *  under-count practice days, never invent one; a latched badge stays). */
export async function loadBadgeSources(
  kidId: string | null | undefined,
  localChildId: string | null | undefined,
  deps: LoadBadgeSourcesDeps = {},
): Promise<BadgeSources> {
  // MA-1202 (D-1103-10): the tree's full-history read. A badge's earn rule is
  // the tree tier (rules.ts treeTierOf), so the 500-row cross-skill window
  // could award gold late or never (the Mastered day's cross-day partner left
  // the window). A failed read degrades to no cloud rows, as before.
  const fetchAttempts =
    deps.fetchAttempts ??
    (async (id: string) => {
      try {
        return deps.readTierHistory ? await deps.readTierHistory(id) : await fetchTierHistoryForChildOrThrow(id);
      } catch (error) {
        console.error("[badges] attempt history read failed:", error);
        return [];
      }
    });
  const fetchEvents = (id: string) =>
    deps.eventsClient
      ? fetchAllMilestoneEventsOfTypesOrThrow(id, [...BADGE_SOURCE_EVENT_TYPES], undefined, deps.eventsClient)
      : fetchAllMilestoneEventsOfTypesOrThrow(id, [...BADGE_SOURCE_EVENT_TYPES]);
  const cloudAttempts = kidId ? await fetchAttempts(kidId) : [];
  let cloudEvents: MilestoneEvent[] = [];
  let cloudEventsFailed = false;
  if (kidId) {
    try {
      cloudEvents = await fetchEvents(kidId);
    } catch (error) {
      // MA-895 (D-1020-7): reported, not swallowed — see BadgeSources.cloudEventsFailed.
      cloudEventsFailed = true;
      console.error("[badges] cloud badge-source read failed; using the local mirror:", error);
    }
  }
  return { attempts: mergeAttempts(cloudAttempts, loadLocalAttempts(localChildId, Number.MAX_SAFE_INTEGER)), cloudEvents, cloudEventsFailed };
}

export interface BadgeState {
  book: BadgeBook;
  /** Every badge record latched by a badge_earned row (cloud ∪ local). */
  latchedIds: string[];
  /** Records the book holds now that no badge_earned row latches yet. */
  toLatch: string[];
  /** Every record held now: the book's earned ids ∪ latched. */
  heldIds: string[];
}

export type BadgeStateInput = Omit<BadgeBookInput, "sessionCompletedAt" | "latchedIds"> & {
  cloudEvents: readonly MilestoneEvent[];
  localEvents: readonly MilestoneEvent[];
};

/** Derived-now ∪ latched: MA-891's buildBadgeBook over the merged events. */
export function computeBadgeState(input: BadgeStateInput): BadgeState {
  const { cloudEvents, localEvents, ...rest } = input;
  const merged = mergeMilestones(cloudEvents, localEvents);
  const latched = latchedBadgeIdsFromEvents(merged);
  const book = buildBadgeBook({ ...rest, sessionCompletedAt: sessionCompletedTimes(merged), latchedIds: latched });
  const toLatch = [...new Set(book.earnedIds)].filter((id) => !latched.has(id));
  const heldIds = [...new Set([...book.earnedIds, ...latched])];
  return { book, latchedIds: [...latched], toLatch, heldIds };
}

/** Every local mirror row the badge book reads (no row limit: badge rows ride
 *  above the mirror's CAP). */
export function loadLocalBadgeSourceEvents(localChildId: string | null | undefined): MilestoneEvent[] {
  return loadLocalMilestones(localChildId, undefined, Number.POSITIVE_INFINITY).filter(
    (e) => e.eventType === "session_completed" || e.eventType === "badge_earned",
  );
}

export interface LatchBadgesArgs {
  ids: readonly string[];
  /** app.activeChildId: the local mirror key. */
  localChildId: string | null | undefined;
  /** The canonical cloud students.id, or null for an unlinked child. */
  canonicalChildId: string | null | undefined;
  /** Whether a cloud write may be attempted now (a real auth session). */
  cloudEnabled: boolean;
  /** Test seam; defaults to the real logMilestoneEvent. */
  log?: (e: MilestoneEvent) => Promise<boolean>;
}

/** Write one badge_earned row per id: the local mirror first (deduped per
 *  badge), then the cloud upsert (keyed childId:badge_earned:<badgeId>). A
 *  failed or skipped cloud write is uploaded later by the outbox drain. */
export async function latchBadges(args: LatchBadgesArgs): Promise<number> {
  const log = args.log ?? logMilestoneEvent;
  const owner = args.canonicalChildId ?? args.localChildId;
  if (!owner) return 0;
  // MA-895 (RED round 1 SHOULD-3): EVERY record is written to the local mirror
  // first, in one synchronous pass, and only then do the cloud writes start. A
  // reload (or a recap that is left) while a cloud write is pending can then
  // never leave a later record unlatched on this device, so it is never
  // revealed a second time.
  const events = args.ids.map((badgeId) => buildBadgeEarnedEvent({ childId: owner, badgeId }));
  for (const event of events) appendLocalMilestone(args.localChildId, event);
  if (args.cloudEnabled && args.canonicalChildId) {
    for (const event of events) {
      const ok = await log(event);
      if (ok) markMilestonesConfirmed(args.localChildId, [event]);
    }
  }
  return events.length;
}

// ── "newly earned since the child last opened the book" (per child, local) ──

const SEEN_PREFIX = "pencilwise-badges-seen-";

/** The badge records the child held when they last opened the book, or null
 *  when the book was never opened on this device (then every held badge is
 *  new). Never throws. */
export function readBadgesSeen(localChildId: string | null | undefined): Set<string> | null {
  if (!localChildId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${SEEN_PREFIX}${localChildId}`);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((x): x is string => typeof x === "string")) : null;
  } catch {
    return null;
  }
}

/** Record that the child opened the book holding `heldIds`. Best-effort. */
export function writeBadgesSeen(localChildId: string | null | undefined, heldIds: readonly string[]): void {
  if (!localChildId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${SEEN_PREFIX}${localChildId}`, JSON.stringify([...new Set(heldIds)].sort()));
  } catch {
    /* best-effort */
  }
}

/** Held records the child has not seen in the book yet. */
export function newlyEarnedSince(heldIds: readonly string[], seen: ReadonlySet<string> | null): string[] {
  return heldIds.filter((id) => !(seen ?? new Set<string>()).has(id));
}
